import { useState } from "react";
import { RefreshCw, Search, Code2, X } from "lucide-react";
import { useAzure } from "@/context/AzureContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import AzureIcon from "@/components/azure/AzureIcon";

export function JsonDrawer({ resource, onClose }) {
  if (!resource) return null;
  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-card border-l border-border shadow-2xl z-50 flex flex-col" data-testid="az-json-view">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2"><Code2 className="h-4 w-4 text-[#0078d4]" /><span className="font-display font-bold">JSON View</span></div>
        <Button size="icon" variant="ghost" onClick={onClose}><X className="h-4 w-4" /></Button>
      </div>
      <pre className="flex-1 overflow-auto p-4 text-xs font-mono bg-[#0b0b0c] text-emerald-100/90">{JSON.stringify(resource, null, 2)}</pre>
    </div>
  );
}

const shortType = (t) => t.split("/").slice(-1)[0];

const PROP_LABELS = {
  size: "Size", vcpus: "vCPUs", ram_gb: "RAM (GB)", os_type: "OS", image: "Image",
  power_state: "Power state", private_ip: "Private IP", public_ip: "Public IP",
  admin_username: "Admin username", size_gb: "Size (GB)", sku: "SKU", state: "State",
  disk_type: "Disk type", subnet: "Subnet", vnet: "Virtual network", ip_address: "IP address",
  allocation: "Allocation", fqdn: "Server name", version: "Version", status: "Status",
  runtime: "Runtime", default_hostname: "Hostname", linked_database: "Database",
  managed_identity: "Managed identity", kind: "Kind", access_tier: "Access tier",
  public_access: "Public blob access", admin_login: "Admin login", health: "Health",
};

function relatedResources(resources, r) {
  const meJson = JSON.stringify(r.properties || {});
  const out = [];
  for (const x of resources) {
    if (x.id === r.id) continue;
    const xJson = JSON.stringify(x.properties || {});
    // outgoing (r references x) or incoming (x references r), incl. subnet ids that start with a vnet id
    if (meJson.includes(x.id) || xJson.includes(r.id)) out.push(x);
  }
  return out;
}

function ResourceDetail({ resource, resources, onOpen, onClose, subs }) {
  const [showJson, setShowJson] = useState(false);
  if (!resource) return null;
  const p = resource.properties || {};
  const subName = (subs || []).find((s) => s.id === resource.subscription_id)?.name || resource.subscription_id;
  const rows = Object.entries(p).filter(([k, v]) =>
    PROP_LABELS[k] && (typeof v === "string" || typeof v === "number" || typeof v === "boolean") && v !== "" && v != null);
  const related = relatedResources(resources, resource);

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-card border-l border-border shadow-2xl z-50 flex flex-col" data-testid="az-resource-detail">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2 min-w-0">
          <AzureIcon type={resource.type} size={22} />
          <div className="min-w-0">
            <div className="font-display font-bold truncate">{resource.name}</div>
            <div className="text-xs text-muted-foreground">{shortType(resource.type)}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" className="gap-1.5" data-testid="az-detail-json" onClick={() => setShowJson((s) => !s)}><Code2 className="h-4 w-4" /> JSON</Button>
          <Button size="icon" variant="ghost" onClick={onClose} data-testid="az-detail-close"><X className="h-4 w-4" /></Button>
        </div>
      </div>

      {showJson ? (
        <pre className="flex-1 overflow-auto p-4 text-xs font-mono bg-[#0b0b0c] text-emerald-100/90" data-testid="az-detail-json-body">{JSON.stringify(resource, null, 2)}</pre>
      ) : (
        <div className="flex-1 overflow-auto p-5 space-y-5 text-sm">
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Overview</div>
            <div className="space-y-1.5">
              {[["Resource group", resource.resource_group], ["Subscription", subName], ["Location", resource.location],
                ["Provisioning state", resource.provisioning_state], ["Created", (resource.created_at || "").slice(0, 10)]].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-border/50 py-1"><span className="text-muted-foreground">{k}</span><span className="text-right">{v || "—"}</span></div>
              ))}
            </div>
          </div>

          {rows.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Properties</div>
              <div className="space-y-1.5">
                {rows.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 border-b border-border/50 py-1">
                    <span className="text-muted-foreground">{PROP_LABELS[k]}</span>
                    <span className="text-right font-mono text-xs break-all">{String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {Object.keys(resource.tags || {}).length > 0 && (
            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Tags</div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(resource.tags).map(([k, v]) => <span key={k} className="text-xs bg-muted px-2 py-0.5 rounded">{k}: {v}</span>)}
              </div>
            </div>
          )}

          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Related resources ({related.length})</div>
            {related.length === 0 ? <div className="text-xs text-muted-foreground">No linked resources.</div> : (
              <div className="space-y-1">
                {related.map((x) => (
                  <button key={x.id} onClick={() => onOpen(x)} data-testid={`az-related-${x.name}`}
                    className="w-full flex items-center gap-2 rounded-md border border-border px-3 py-2 hover:border-[#0078d4]/50 text-left">
                    <AzureIcon type={x.type} size={16} />
                    <span className="text-sm text-[#0078d4] truncate">{x.name}</span>
                    <span className="ml-auto text-[11px] text-muted-foreground shrink-0">{shortType(x.type)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AllResources() {
  const { state } = useAzure();
  const resources = state?.resources || [];
  const subs = state?.subscriptions || [];
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [selected, setSelected] = useState(null);

  const types = ["all", ...Array.from(new Set(resources.map((r) => r.type)))];
  const filtered = resources.filter((r) => {
    const matchQ = (r.name + r.type + r.resource_group).toLowerCase().includes(q.toLowerCase());
    const matchT = type === "all" || r.type === type;
    return matchQ && matchT;
  });

  return (
    <div data-testid="az-all-resources">
      <div className="flex items-center gap-2 border-b border-border pb-3 mb-3 flex-wrap">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-56 h-9" data-testid="az-res-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t === "all" ? "All types" : shortType(t)}</SelectItem>)}</SelectContent>
        </Select>
        <div className="relative ml-auto">
          <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="az-res-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name" className="pl-8 h-9 w-64" />
        </div>
      </div>
      <div className="border border-border rounded-md overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Resource group</TableHead><TableHead>Location</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {filtered.map((r) => (
              <TableRow key={r.id} data-testid={`az-res-row-${r.name}`}>
                <TableCell className="font-medium">
                  <button className="inline-flex items-center gap-2 text-[#0078d4] hover:underline" data-testid={`az-res-open-${r.name}`} onClick={() => setSelected(r)}>
                    <AzureIcon type={r.type} size={18} />{r.name}
                  </button>
                </TableCell>
                <TableCell className="text-xs">{shortType(r.type)}</TableCell>
                <TableCell>{r.resource_group}</TableCell>
                <TableCell>{r.location}</TableCell>
                <TableCell><Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`az-json-${r.name}`} onClick={() => setSelected(r)}><Code2 className="h-4 w-4" /></Button></TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No resources. Deploy a VM or create a network to get started.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <ResourceDetail resource={selected} resources={resources} subs={subs} onOpen={setSelected} onClose={() => setSelected(null)} />
    </div>
  );
}
